import { useEffect } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, PlayCircle } from 'lucide-react';
import { VideoCard, VideoPlayer } from '@/components/video';
import { AUDIENCE_LABELS, GUIDES, guidePath, type GuideAudience } from '@/content/guides';
import { VIDEOS, findVideo, type Video } from '@/content/videos';

/*
 * Video tutorials (content/videos), public: /videos lists them (filter by
 * audience, ?for=), /videos/:video plays one with captions and a transcript,
 * next to the guide chapters it belongs with.
 */

const eyebrow =
  'flex items-center gap-2 text-xs font-semibold tracking-wider text-primary uppercase';

const ROLE_ORDER: GuideAudience[] = ['learners', 'admins', 'authors', 'organizations'];

function VideoGrid({ videos }: { videos: Video[] }) {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {videos.map((v) => (
        <li key={v.slug}>
          <VideoCard video={v} />
        </li>
      ))}
    </ul>
  );
}

export function VideosPage() {
  const [params, setParams] = useSearchParams();
  const audiences = [...new Set(VIDEOS.flatMap((v) => v.audiences))];
  const audience = audiences.find((a) => a === params.get('for')) as GuideAudience | undefined;
  const shown = audience ? VIDEOS.filter((v) => v.audiences.includes(audience)) : VIDEOS;
  const chip = (active: boolean) =>
    `min-h-10 shrink-0 rounded-full border px-4 text-sm ${active ? 'border-primary bg-primary/10 font-semibold text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`;
  return (
    <div className="space-y-6">
      <Link
        to="/resources"
        className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        ← Resources
      </Link>
      <header className="max-w-3xl space-y-2">
        <p className={eyebrow}>
          <PlayCircle className="size-4" aria-hidden /> Videos
        </p>
        <h1 className="font-display text-3xl font-semibold">Video tutorials</h1>
        <p className="text-muted-foreground">
          Short walk-throughs of Nirvik Ready, with captions and a transcript. Narrated with an AI
          voice.
        </p>
      </header>
      {audiences.length > 1 && (
        <div
          role="group"
          aria-label="Who it’s for"
          className="-mx-4 flex gap-2 overflow-x-auto px-4"
        >
          <button type="button" className={chip(!audience)} onClick={() => setParams({})}>
            All
          </button>
          {audiences.map((a) => (
            <button
              key={a}
              type="button"
              aria-pressed={audience === a}
              className={chip(audience === a)}
              onClick={() => setParams({ for: a })}
            >
              {AUDIENCE_LABELS[a]}
            </button>
          ))}
        </div>
      )}
      {!shown.length ? (
        <p className="text-sm text-muted-foreground">No videos yet.</p>
      ) : audience ? (
        <VideoGrid videos={shown} />
      ) : (
        // Everything: a section per role, each video under its first audience.
        ROLE_ORDER.map((role) => {
          const videos = VIDEOS.filter((v) => v.audiences[0] === role);
          return videos.length ? (
            <section key={role} className="space-y-3">
              <h2 className="font-display text-xl font-semibold">
                For {AUDIENCE_LABELS[role].toLowerCase()}
              </h2>
              <VideoGrid videos={videos} />
            </section>
          ) : null;
        })
      )}
    </div>
  );
}

/**
 * The guide chapters a video belongs with. A bare chapter slug is shown once,
 * from the first guide that has it and is for one of the video's audiences.
 */
function chaptersOf(video: Video) {
  return video.chapters.flatMap((entry) => {
    const [g, c] = entry.includes('/') ? entry.split('/') : [null, entry];
    const guide = g
      ? GUIDES.find((x) => x.slug === g)
      : (GUIDES.find(
          (x) =>
            x.audiences.some((a) => video.audiences.includes(a)) &&
            x.chapters.some((ch) => ch.slug === c),
        ) ?? GUIDES.find((x) => x.chapters.some((ch) => ch.slug === c)));
    const chapter = guide?.chapters.find((ch) => ch.slug === c);
    return guide && chapter ? [{ guide, chapter }] : [];
  });
}

export function VideoPage() {
  const { video: slug } = useParams();
  const video = findVideo(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!video) return <Navigate to="/videos" replace />;
  const chapters = chaptersOf(video);
  const more = VIDEOS.filter(
    (v) => v.slug !== video.slug && v.audiences.some((a) => video.audiences.includes(a)),
  ).slice(0, 3);
  return (
    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <article className="min-w-0 space-y-4">
        <Link
          to="/videos"
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Videos
        </Link>
        <h1 className="font-display text-2xl font-semibold sm:text-3xl">{video.title}</h1>
        <p className="text-muted-foreground">{video.summary}</p>
        <VideoPlayer video={video} />
      </article>
      <aside className="space-y-6">
        {chapters.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Read more
            </h2>
            <ul className="space-y-2">
              {chapters.map(({ guide, chapter }) => (
                <li key={`${guide.slug}/${chapter.slug}`}>
                  <Link
                    to={guidePath(guide.slug, chapter.slug)}
                    className="flex gap-3 rounded-xl border border-border bg-card p-3 hover:border-primary"
                  >
                    <BookOpen className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
                    <span>
                      <span className="block text-sm font-semibold">{chapter.title}</span>
                      <span className="block text-xs text-muted-foreground">{guide.title}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {more.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              More videos
            </h2>
            <ul className="space-y-3">
              {more.map((v) => (
                <li key={v.slug}>
                  <VideoCard video={v} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>
    </div>
  );
}
